// src/constants.ts
var PROMO_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  ACTIVE: "active",
  PAUSED: "paused",
  EXPIRED: "expired",
  CANCELLED: "cancelled"
};
var DRIVERSTOPROMOS_STATUS = {
  ACTIVE: "active",
  FULFILLED: "fulfilled",
  CANCELLED: "cancelled",
  VOIDED: "voided",
  FAILED: "failed"
};
var COUNTING_STATUSES = [
  DRIVERSTOPROMOS_STATUS.ACTIVE,
  DRIVERSTOPROMOS_STATUS.FULFILLED
];
var TERMINAL_DROP_STATUSES = [
  DRIVERSTOPROMOS_STATUS.CANCELLED,
  DRIVERSTOPROMOS_STATUS.VOIDED,
  DRIVERSTOPROMOS_STATUS.FAILED
];
var COMPLETION_OUTCOME = {
  SETTLED: "settled",
  HOLD: "hold"
};
var HOLD_IN_FLIGHT_REASONS = [
  "window_open",
  "moves_in_progress",
  "moves_unsettled"
];
var HOLD_INDETERMINATE_REASONS = [
  "terminal_status",
  "invalid_parameters",
  "unparseable_begin_time",
  "decline_check_failed",
  "settlement_query_failed"
];
var SETTLED_REASONS = [
  "floor_met",
  "commitment_failed",
  "no_payout_template"
];
var GUARANTEED_PAY_BLOCK_SETTLE_GRACE_HOURS = 6;
var MOVE_STATUSES_NOT_STARTED = ["pending", "dispatched"];
var MOVE_STATUSES_FINISHED = [
  "delivery successful",
  "droppedOff",
  "completed",
  "cancelled",
  "canceled",
  "failed"
];
var MOVE_STATUSES_NOT_IN_PROGRESS = [
  ...MOVE_STATUSES_NOT_STARTED,
  ...MOVE_STATUSES_FINISHED
];
var ALGORITHM_TYPE = {
  ELIGIBILITY: "eligibility",
  COMPLETION: "completion"
};
var ALGORITHM_KEY = {
  PRIORITY_ONLY_BLOCK_ELIGIBILITY_V1: "priority_only_block_eligibility_v1",
  PRIORITY_ONLY_BLOCK_COMPLETION_V1: "priority_only_block_completion_v1",
  GUARANTEED_PAY_BLOCK_ELIGIBILITY_V1: "guaranteed_pay_block_eligibility_v1",
  GUARANTEED_PAY_BLOCK_COMPLETION_V1: "guaranteed_pay_block_completion_v1"
};
var PROMO_TEMPLATE_KEY = {
  PRIORITY_ONLY_BLOCK: "priority_only_block",
  GUARANTEED_PAY_BLOCK: "guaranteed_pay_block"
};
var CUSTOMER_TIER = {
  STANDARD: "standard",
  LITE: "lite",
  HYBRID: "hybrid"
};
var DRIVER_TAX_CLASS = {
  IC: "1099",
  W2: "W-2",
  LITE: "lite"
};
var TEMPLATE_REGISTRY = {
  [PROMO_TEMPLATE_KEY.PRIORITY_ONLY_BLOCK]: {
    key: PROMO_TEMPLATE_KEY.PRIORITY_ONLY_BLOCK,
    label: "Priority Only Time Block",
    shortLabel: "Priority",
    description: "Driver commits to a time window and gets a dispatch boost on matching moves. No floor pay \u2014 driver earns standard move pay only.",
    driverDescription: "You'll get boosted dispatch priority on matching moves during this window. No floor pay \u2014 you earn standard move pay only.",
    iconHint: "bolt",
    algorithms: {
      eligibility: ALGORITHM_KEY.PRIORITY_ONLY_BLOCK_ELIGIBILITY_V1,
      completion: ALGORITHM_KEY.PRIORITY_ONLY_BLOCK_COMPLETION_V1
    },
    boost: { default: 2, allowOverride: true },
    badge: {
      title: "PRIORITY ACCESS",
      subtitle: "First on deck for moves during this window. No earnings floor."
    }
  },
  [PROMO_TEMPLATE_KEY.GUARANTEED_PAY_BLOCK]: {
    key: PROMO_TEMPLATE_KEY.GUARANTEED_PAY_BLOCK,
    label: "Guaranteed Pay Block",
    shortLabel: "Guaranteed Pay",
    description: "Driver commits to a time window with a guaranteed floor amount. Move pay during the window counts toward the floor; any shortfall is topped up. Declining a move offer during the window voids the guarantee (promo-only offers are ignored).",
    driverDescription: "You're guaranteed a floor amount for this window. Move pay counts toward the floor \u2014 if you fall short, we top you up. Declining a move offer during the window voids the guarantee.",
    iconHint: "paid",
    algorithms: {
      eligibility: ALGORITHM_KEY.GUARANTEED_PAY_BLOCK_ELIGIBILITY_V1,
      completion: ALGORITHM_KEY.GUARANTEED_PAY_BLOCK_COMPLETION_V1
    },
    boost: { default: 2, allowOverride: true },
    badge: {
      title: "GUARANTEED PAY",
      subtitle: "Floor pay for this window. Declining a move offer voids the guarantee."
    }
  }
};
var TEMPLATE_BOOST_CONFIG = {
  [PROMO_TEMPLATE_KEY.PRIORITY_ONLY_BLOCK]: TEMPLATE_REGISTRY[PROMO_TEMPLATE_KEY.PRIORITY_ONLY_BLOCK].boost,
  [PROMO_TEMPLATE_KEY.GUARANTEED_PAY_BLOCK]: TEMPLATE_REGISTRY[PROMO_TEMPLATE_KEY.GUARANTEED_PAY_BLOCK].boost
};
var TEMPLATE_EXCLUDED_TAX_CLASSES = {
  [PROMO_TEMPLATE_KEY.PRIORITY_ONLY_BLOCK]: [DRIVER_TAX_CLASS.LITE],
  [PROMO_TEMPLATE_KEY.GUARANTEED_PAY_BLOCK]: [DRIVER_TAX_CLASS.LITE, DRIVER_TAX_CLASS.W2]
};
var ELIGIBILITY_REASON = {
  MISSING_CERTIFICATIONS: "missing_certifications",
  INVALID_PARAMETERS: "invalid_parameters",
  INELIGIBLE_TAX_CLASS: "ineligible_tax_class"
};
var GUARANTEED_PAY_BLOCK_FAILURE_OFFER_STATUSES = ["declined"];

// src/boost.ts
function resolveBoostWeight(promo) {
  const templateKey = promo.promo_template_key;
  if (!templateKey) return 0;
  const config = TEMPLATE_BOOST_CONFIG[templateKey];
  if (!config) return 0;
  const boost = promo.parameters?.boost;
  if (typeof boost?.weight !== "number") return 0;
  return boost.weight;
}

// src/algorithms/priorityOnlyBlock/eligibility.ts
var EXCLUDED_TAX_CLASSES = TEMPLATE_EXCLUDED_TAX_CLASSES[PROMO_TEMPLATE_KEY.PRIORITY_ONLY_BLOCK];
function parseParameters(parameters) {
  if (parameters === null || typeof parameters !== "object" || Array.isArray(parameters)) {
    return null;
  }
  const certs = parameters.certifications;
  if (certs !== void 0) {
    if (!Array.isArray(certs) || !certs.every((c) => typeof c === "string")) {
      return null;
    }
  }
  return parameters;
}
var eligibility = async (promo, ctx) => {
  const params = parseParameters(promo.parameters);
  if (params === null) {
    return { eligible: false, reason: ELIGIBILITY_REASON.INVALID_PARAMETERS };
  }
  const taxClass = ctx.driver.tax_class;
  if (taxClass != null && EXCLUDED_TAX_CLASSES.includes(taxClass)) {
    return { eligible: false, reason: ELIGIBILITY_REASON.INELIGIBLE_TAX_CLASS };
  }
  const requiredCerts = params.certifications ?? [];
  if (requiredCerts.length > 0) {
    const hasAllCerts = requiredCerts.every((c) => ctx.driver.certifications.includes(c));
    if (!hasAllCerts) {
      return { eligible: false, reason: ELIGIBILITY_REASON.MISSING_CERTIFICATIONS };
    }
  }
  return { eligible: true };
};

// src/algorithms/priorityOnlyBlock/completion.ts
var completion = async (_promo, ctx) => {
  const { driverstopromo } = ctx;
  if (TERMINAL_DROP_STATUSES.includes(driverstopromo.status)) {
    return {
      outcome: COMPLETION_OUTCOME.HOLD,
      newStatus: driverstopromo.status,
      payouts: [],
      reason: "terminal_status"
    };
  }
  const endTime = driverstopromo.end_time ? new Date(driverstopromo.end_time) : null;
  if (!endTime || endTime > /* @__PURE__ */ new Date()) {
    return {
      outcome: COMPLETION_OUTCOME.HOLD,
      newStatus: driverstopromo.status,
      payouts: [],
      reason: "window_open"
    };
  }
  return {
    outcome: COMPLETION_OUTCOME.SETTLED,
    newStatus: DRIVERSTOPROMOS_STATUS.FULFILLED,
    payouts: [],
    reason: "no_payout_template"
  };
};

// src/algorithms/guaranteedPayBlock/parameters.ts
function parseBoost(value) {
  if (value === void 0) return void 0;
  if (value === null || typeof value !== "object" || Array.isArray(value)) return void 0;
  const obj = value;
  if (!("weight" in obj)) return {};
  if (!Number.isFinite(obj.weight)) return void 0;
  return { weight: obj.weight };
}
function parseCertifications(value) {
  if (value === void 0) return void 0;
  if (!Array.isArray(value)) return void 0;
  if (!value.every((c) => typeof c === "string")) return void 0;
  return value;
}
function parseGuaranteedPayBlockParameters(parameters) {
  if (parameters === null || typeof parameters !== "object" || Array.isArray(parameters)) {
    return null;
  }
  const obj = parameters;
  if (!Number.isFinite(obj.guaranteed_amount) || obj.guaranteed_amount <= 0) {
    return null;
  }
  const certifications = parseCertifications(obj.certifications);
  if (obj.certifications !== void 0 && certifications === void 0) return null;
  const boost = parseBoost(obj.boost);
  if (obj.boost !== void 0 && boost === void 0) return null;
  return {
    guaranteed_amount: obj.guaranteed_amount,
    ...certifications !== void 0 && { certifications },
    ...boost !== void 0 && { boost }
  };
}

// src/algorithms/guaranteedPayBlock/eligibility.ts
var EXCLUDED_TAX_CLASSES2 = TEMPLATE_EXCLUDED_TAX_CLASSES[PROMO_TEMPLATE_KEY.GUARANTEED_PAY_BLOCK];
var eligibility2 = async (promo, ctx) => {
  const params = parseGuaranteedPayBlockParameters(promo.parameters);
  if (params === null) {
    return { eligible: false, reason: ELIGIBILITY_REASON.INVALID_PARAMETERS };
  }
  const taxClass = ctx.driver.tax_class;
  if (taxClass != null && EXCLUDED_TAX_CLASSES2.includes(taxClass)) {
    return { eligible: false, reason: ELIGIBILITY_REASON.INELIGIBLE_TAX_CLASS };
  }
  const requiredCerts = params.certifications ?? [];
  if (requiredCerts.length > 0) {
    const hasAllCerts = requiredCerts.every((c) => ctx.driver.certifications.includes(c));
    if (!hasAllCerts) {
      return { eligible: false, reason: ELIGIBILITY_REASON.MISSING_CERTIFICATIONS };
    }
  }
  return { eligible: true };
};

// ../../gql/operations.ts
import { resolveExecutor } from "@hopdrive/sdk-core";

// ../../gql/schema.ts
var GetDeclinedWorkOffersInWindowDocument = { "__text": "query getDeclinedWorkOffersInWindow($driverId: bigint!, $beginTime: timestamptz!, $endTime: timestamptz!, $statuses: [String!]!) {\n  declinedWorkOffers: offers_aggregate(\n    where: {driver_id: {_eq: $driverId}, status: {_in: $statuses}, bundle: {scenarios: {scenario: {moves: {move: {active: {_eq: 1}, pickup_time: {_gte: $beginTime, _lt: $endTime}}}}}}}\n  ) {\n    aggregate {\n      count\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getDeclinedWorkOffersInWindow" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "driverId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "beginTime" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "endTime" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "statuses" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "alias": { "kind": "Name", "value": "declinedWorkOffers" }, "name": { "kind": "Name", "value": "offers_aggregate" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driverId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_in" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "statuses" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "bundle" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenarios" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "scenario" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "moves" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "move" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_time" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "beginTime" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_lt" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "endTime" } } }] } }] } }] } }] } }] } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "count" } }] } }] } }] } }] };
var GetMovesForGuaranteedPayBlockSettlementDocument = { "__text": 'query getMovesForGuaranteedPayBlockSettlement($driverId: bigint!, $beginTime: timestamptz!, $endTime: timestamptz!, $notInProgressStatuses: [String!]!) {\n  inProgressMoves: moves_aggregate(\n    where: {driver_id: {_eq: $driverId}, pickup_time: {_gte: $beginTime, _lt: $endTime}, active: {_eq: 1}, cancel_status: {_is_null: true}, status: {_nin: $notInProgressStatuses}}\n  ) {\n    aggregate {\n      count\n    }\n  }\n  unsettledMoves: moves_aggregate(\n    where: {driver_id: {_eq: $driverId}, pickup_time: {_gte: $beginTime, _lt: $endTime}, active: {_eq: 1}, _not: {appayments: {type: {_eq: "move pay"}}}}\n  ) {\n    aggregate {\n      count\n    }\n  }\n  paidMovePay: appayments_aggregate(\n    where: {type: {_eq: "move pay"}, active: {_eq: 1}, move: {driver_id: {_eq: $driverId}, pickup_time: {_gte: $beginTime, _lt: $endTime}, active: {_eq: 1}}}\n  ) {\n    aggregate {\n      sum {\n        amount\n      }\n    }\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getMovesForGuaranteedPayBlockSettlement" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "driverId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "beginTime" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "endTime" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "timestamptz" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "notInProgressStatuses" } }, "type": { "kind": "NonNullType", "type": { "kind": "ListType", "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "alias": { "kind": "Name", "value": "inProgressMoves" }, "name": { "kind": "Name", "value": "moves_aggregate" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driverId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_time" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "beginTime" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_lt" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "endTime" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "cancel_status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_is_null" }, "value": { "kind": "BooleanValue", "value": true } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_nin" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "notInProgressStatuses" } } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "count" } }] } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "unsettledMoves" }, "name": { "kind": "Name", "value": "moves_aggregate" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driverId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_time" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "beginTime" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_lt" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "endTime" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_not" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "appayments" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "move pay", "block": false } }] } }] } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "count" } }] } }] } }, { "kind": "Field", "alias": { "kind": "Name", "value": "paidMovePay" }, "name": { "kind": "Name", "value": "appayments_aggregate" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "move pay", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "move" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driverId" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "pickup_time" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_gte" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "beginTime" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "_lt" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "endTime" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "IntValue", "value": "1" } }] } }] } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "aggregate" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "sum" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "amount" } }] } }] } }] } }] } }] };
var GetRegionTimezoneDocument = { "__text": "query getRegionTimezone($regionId: bigint!) {\n  regions_by_pk(id: $regionId) {\n    timezone\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getRegionTimezone" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "regionId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "regions_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "regionId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "timezone" } }] } }] } }] };
var ExpirePromoDocument = { "__text": "mutation expirePromo($id: bigint!, $expired: String!) {\n  update_promos_by_pk(pk_columns: {id: $id}, _set: {status: $expired}) {\n    id\n    status\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "expirePromo" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "expired" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "update_promos_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "pk_columns" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "_set" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "expired" } } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }] } }] } }] };
var GetPromoForExpirationDocument = { "__text": "query getPromoForExpiration($id: bigint!) {\n  promos_by_pk(id: $id) {\n    id\n    status\n    end_time\n    max_acceptances\n    accepted_count\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getPromoForExpiration" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "promos_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "end_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "max_acceptances" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_count" } }] } }] } }] };
var GetDriverstopromoForPayoutDocument = { "__text": "query getDriverstopromoForPayout($id: bigint!) {\n  driverstopromos_by_pk(id: $id) {\n    id\n    driver_id\n    promo_id\n    bundle_id\n    status\n    begin_time\n    end_time\n    offered_at\n    accepted_at\n    promo {\n      id\n      promo_template_key\n      status\n      eligibility_algorithm_key\n      completion_algorithm_key\n      parameters\n      requires_acceptance\n      max_acceptances\n      accepted_count\n      scope_region_id\n      scope_organization_id\n      scope_customer_id\n      begin_time\n      end_time\n    }\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getDriverstopromoForPayout" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "driverstopromos_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "id" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "driver_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "promo_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "bundle_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "begin_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "end_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "offered_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_at" } }, { "kind": "Field", "name": { "kind": "Name", "value": "promo" }, "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "promo_template_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "status" } }, { "kind": "Field", "name": { "kind": "Name", "value": "eligibility_algorithm_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "completion_algorithm_key" } }, { "kind": "Field", "name": { "kind": "Name", "value": "parameters" } }, { "kind": "Field", "name": { "kind": "Name", "value": "requires_acceptance" } }, { "kind": "Field", "name": { "kind": "Name", "value": "max_acceptances" } }, { "kind": "Field", "name": { "kind": "Name", "value": "accepted_count" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scope_region_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scope_organization_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "scope_customer_id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "begin_time" } }, { "kind": "Field", "name": { "kind": "Name", "value": "end_time" } }] } }] } }] } }] };
var GetExistingPromoAppaymentDocument = { "__text": 'query getExistingPromoAppayment($driver_id: bigint!, $promo_id: bigint!, $notes: String!) {\n  appayments(\n    where: {driver_id: {_eq: $driver_id}, promo_id: {_eq: $promo_id}, move_id: {_is_null: true}, type: {_eq: "promo"}, notes: {_eq: $notes}}\n    limit: 1\n  ) {\n    id\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getExistingPromoAppayment" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "driver_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "promo_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "notes" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "appayments" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driver_id" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "promo_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "promo_id" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "move_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_is_null" }, "value": { "kind": "BooleanValue", "value": true } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "promo", "block": false } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "notes" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "notes" } } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] };
var GetPromoWindowAppaymentDocument = { "__text": 'query getPromoWindowAppayment($driver_id: bigint!, $promo_id: bigint!) {\n  appayments(\n    where: {driver_id: {_eq: $driver_id}, promo_id: {_eq: $promo_id}, move_id: {_is_null: true}, type: {_eq: "promo"}}\n    limit: 1\n  ) {\n    id\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "getPromoWindowAppayment" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "driver_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "promo_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "appayments" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "where" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driver_id" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "promo_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "promo_id" } } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "move_id" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_is_null" }, "value": { "kind": "BooleanValue", "value": true } }] } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "type" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "_eq" }, "value": { "kind": "StringValue", "value": "promo", "block": false } }] } }] } }, { "kind": "Argument", "name": { "kind": "Name", "value": "limit" }, "value": { "kind": "IntValue", "value": "1" } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] };
var InsertPromoAppaymentDocument = { "__text": 'mutation insertPromoAppayment($driver_id: bigint!, $promo_id: bigint!, $amount: numeric!, $notes: String!, $author: String!, $source_event_id: String, $status: String!, $active: smallint!) {\n  insert_appayments_one(\n    object: {driver_id: $driver_id, promo_id: $promo_id, amount: $amount, notes: $notes, author: $author, source_event_id: $source_event_id, type: "promo", createdat: "now()", active: $active, status: $status}\n  ) {\n    id\n  }\n}', "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "mutation", "name": { "kind": "Name", "value": "insertPromoAppayment" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "driver_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "promo_id" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "amount" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "numeric" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "notes" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "author" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "source_event_id" } }, "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "String" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "active" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "smallint" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "insert_appayments_one" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "object" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "driver_id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "driver_id" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "promo_id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "promo_id" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "amount" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "amount" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "notes" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "notes" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "author" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "author" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "source_event_id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "source_event_id" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "type" }, "value": { "kind": "StringValue", "value": "promo", "block": false } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "createdat" }, "value": { "kind": "StringValue", "value": "now()", "block": false } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "active" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "active" } } }, { "kind": "ObjectField", "name": { "kind": "Name", "value": "status" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "status" } } }] } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }] } }] } }] };

// ../../gql/operations.ts
async function expirePromo(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).mutate(
    ExpirePromoDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.update_promos_by_pk;
}
async function getDeclinedWorkOffersInWindow(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).query(
    GetDeclinedWorkOffersInWindowDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.declinedWorkOffers;
}
async function getDriverstopromoForPayout(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).query(
    GetDriverstopromoForPayoutDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.driverstopromos_by_pk;
}
async function getExistingPromoAppayment(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).query(
    GetExistingPromoAppaymentDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.appayments;
}
async function getMovesForGuaranteedPayBlockSettlement(variables, options) {
  const { executor, ...requestOptions } = options ?? {};
  return resolveExecutor(executor).query(
    GetMovesForGuaranteedPayBlockSettlementDocument,
    variables,
    requestOptions
  );
}
async function getPromoForExpiration(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).query(
    GetPromoForExpirationDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.promos_by_pk;
}
async function getPromoWindowAppayment(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).query(
    GetPromoWindowAppaymentDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.appayments;
}
async function getRegionTimezone(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).query(
    GetRegionTimezoneDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.regions_by_pk;
}
async function insertPromoAppayment(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor(executor).mutate(
    InsertPromoAppaymentDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.insert_appayments_one;
}

// src/algorithms/guaranteedPayBlock/completion.ts
var CENTS_PER_DOLLAR = 100;
var MIN_PAYABLE_DOLLARS = 0.01;
var MS_PER_HOUR = 60 * 60 * 1e3;
var hold = (dtp, reason) => ({
  outcome: COMPLETION_OUTCOME.HOLD,
  newStatus: dtp.status,
  payouts: [],
  reason
});
var windowHasNotEnded = (dtp) => {
  if (!dtp.end_time) return true;
  return new Date(dtp.end_time) > /* @__PURE__ */ new Date();
};
var settleGraceElapsed = (dtp) => {
  if (!dtp.end_time) return false;
  const deadline = new Date(dtp.end_time).getTime() + GUARANTEED_PAY_BLOCK_SETTLE_GRACE_HOURS * MS_PER_HOUR;
  return Date.now() > deadline;
};
async function fetchDeclinedWorkOfferCount(ctx) {
  const { driverstopromo, client, logger } = ctx;
  try {
    const result = await getDeclinedWorkOffersInWindow(
      {
        driverId: driverstopromo.driver_id,
        beginTime: driverstopromo.begin_time,
        endTime: driverstopromo.end_time,
        statuses: [...GUARANTEED_PAY_BLOCK_FAILURE_OFFER_STATUSES]
      },
      { executor: client }
    );
    return result.aggregate?.count ?? 0;
  } catch (err) {
    logger?.warn("Decline check query failed; holding row for next sweep", {
      driverstopromoId: driverstopromo.id,
      error: err
    });
    return null;
  }
}
async function fetchSettlement(ctx) {
  const { driverstopromo, client, logger } = ctx;
  try {
    const result = await getMovesForGuaranteedPayBlockSettlement(
      {
        driverId: driverstopromo.driver_id,
        beginTime: driverstopromo.begin_time,
        endTime: driverstopromo.end_time,
        notInProgressStatuses: [...MOVE_STATUSES_NOT_IN_PROGRESS]
      },
      { executor: client }
    );
    return {
      inProgressCount: result.inProgressMoves.aggregate?.count ?? 0,
      unsettledCount: result.unsettledMoves.aggregate?.count ?? 0,
      paidSum: Number(result.paidMovePay.aggregate?.sum?.amount ?? 0)
    };
  } catch (err) {
    logger?.warn("Guarantee settlement query failed; holding row for next sweep", {
      driverstopromoId: driverstopromo.id,
      error: err
    });
    return null;
  }
}
async function resolveMarketTimeZone(client, promo, logger) {
  if (promo.scope_region_id == null) {
    logger?.warn("Guaranteed pay block has no region scope; falling back to UTC for date label", {
      promoId: promo.id
    });
    return null;
  }
  try {
    const region = await getRegionTimezone(
      { regionId: promo.scope_region_id },
      { executor: client }
    );
    if (!region?.timezone) {
      logger?.warn("Region has no timezone configured; falling back to UTC for date label", {
        promoId: promo.id,
        regionId: promo.scope_region_id
      });
      return null;
    }
    return region.timezone;
  } catch (err) {
    logger?.warn("Region timezone lookup failed; falling back to UTC for date label", {
      promoId: promo.id,
      regionId: promo.scope_region_id,
      error: err
    });
    return null;
  }
}
function formatBlockDateLabel(beginTime, timeZone) {
  const d = new Date(beginTime);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone });
  } catch {
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
  }
}
function roundToCents(amount) {
  return Math.round(amount * CENTS_PER_DOLLAR) / CENTS_PER_DOLLAR;
}
async function buildFulfillmentResult(params, settlement, promo, ctx) {
  const { driverstopromo, client, logger } = ctx;
  const delta = roundToCents(params.guaranteed_amount - settlement.paidSum);
  if (delta < MIN_PAYABLE_DOLLARS) {
    return {
      outcome: COMPLETION_OUTCOME.SETTLED,
      newStatus: DRIVERSTOPROMOS_STATUS.FULFILLED,
      payouts: [],
      reason: "floor_met"
    };
  }
  const timeZone = await resolveMarketTimeZone(client, promo, logger) ?? "UTC";
  const dateLabel = formatBlockDateLabel(driverstopromo.begin_time, timeZone);
  if (dateLabel === null) {
    logger?.warn("Unparseable begin_time on driverstopromo; not writing payout", {
      driverstopromoId: driverstopromo.id,
      beginTime: driverstopromo.begin_time
    });
    return hold(driverstopromo, "unparseable_begin_time");
  }
  const payouts = [{ amount: delta, notes: `Guaranteed pay - ${dateLabel}` }];
  return {
    outcome: COMPLETION_OUTCOME.SETTLED,
    newStatus: DRIVERSTOPROMOS_STATUS.FULFILLED,
    payouts
  };
}
var completion2 = async (promo, ctx) => {
  const { driverstopromo } = ctx;
  if (TERMINAL_DROP_STATUSES.includes(driverstopromo.status)) {
    return hold(driverstopromo, "terminal_status");
  }
  const params = parseGuaranteedPayBlockParameters(promo.parameters);
  if (params === null) {
    return hold(driverstopromo, "invalid_parameters");
  }
  const stillActive = driverstopromo.status === DRIVERSTOPROMOS_STATUS.ACTIVE;
  if (windowHasNotEnded(driverstopromo)) {
    return hold(driverstopromo, "window_open");
  }
  const settlement = await fetchSettlement(ctx);
  if (settlement === null) return hold(driverstopromo, "settlement_query_failed");
  if (stillActive && !settleGraceElapsed(driverstopromo)) {
    if (settlement.inProgressCount > 0) return hold(driverstopromo, "moves_in_progress");
    if (settlement.unsettledCount > 0) return hold(driverstopromo, "moves_unsettled");
  }
  const declineCount = await fetchDeclinedWorkOfferCount(ctx);
  if (declineCount === null) return hold(driverstopromo, "decline_check_failed");
  if (declineCount > 0) {
    return {
      outcome: COMPLETION_OUTCOME.SETTLED,
      newStatus: DRIVERSTOPROMOS_STATUS.FAILED,
      payouts: [],
      reason: "commitment_failed"
    };
  }
  return buildFulfillmentResult(params, settlement, promo, ctx);
};

// src/registry.ts
var algorithmRegistry = {
  [ALGORITHM_KEY.PRIORITY_ONLY_BLOCK_ELIGIBILITY_V1]: {
    type: ALGORITHM_TYPE.ELIGIBILITY,
    fn: eligibility
  },
  [ALGORITHM_KEY.PRIORITY_ONLY_BLOCK_COMPLETION_V1]: {
    type: ALGORITHM_TYPE.COMPLETION,
    fn: completion
  },
  [ALGORITHM_KEY.GUARANTEED_PAY_BLOCK_ELIGIBILITY_V1]: {
    type: ALGORITHM_TYPE.ELIGIBILITY,
    fn: eligibility2
  },
  [ALGORITHM_KEY.GUARANTEED_PAY_BLOCK_COMPLETION_V1]: {
    type: ALGORITHM_TYPE.COMPLETION,
    fn: completion2
  }
};
function getAlgorithm(key) {
  const entry = algorithmRegistry[key];
  if (!entry) {
    throw new Error(`Unknown algorithm_key: ${key}`);
  }
  return entry;
}

// src/eligibility.ts
async function runEligibility(promo, ctx) {
  const entry = getAlgorithm(promo.eligibility_algorithm_key);
  if (entry.type !== ALGORITHM_TYPE.ELIGIBILITY) {
    throw new Error(
      `Algorithm ${promo.eligibility_algorithm_key} is not eligibility type (got ${entry.type})`
    );
  }
  return entry.fn(promo, ctx);
}

// src/completion.ts
async function runCompletion(promo, ctx) {
  const entry = getAlgorithm(promo.completion_algorithm_key);
  if (entry.type !== ALGORITHM_TYPE.COMPLETION) {
    throw new Error(
      `Algorithm ${promo.completion_algorithm_key} is not a completion algorithm (got ${entry.type})`
    );
  }
  return entry.fn(promo, ctx);
}

// src/payout/runPromoPayout.ts
import { resolveExecutor as resolveExecutor2 } from "@hopdrive/sdk-core";
var APPAYMENT_AUTHOR_ENGINE = "AP engine";
var APPAYMENT_STATUS_UNPAID = "unpaid";
var APPAYMENT_ACTIVE = 1;
async function runPromoPayout(driverstopromosId, hasuraEventId, options = {}) {
  const { logger } = options;
  const executor = resolveExecutor2(options.executor);
  if (!driverstopromosId) {
    return { success: false, error: "driverstopromosId required" };
  }
  const dtp = await fetchDriverstopromoForPayout(executor, driverstopromosId, logger);
  if (!dtp || !dtp.promo) {
    return { success: false, error: `driverstopromos #${driverstopromosId} or its promo not found` };
  }
  const settledAppaymentId = await findPromoWindowAppayment(executor, dtp, logger);
  if (settledAppaymentId !== null) {
    logger?.info("runPromoPayout: window payout already written; skipping the re-run", {
      driverstopromosId,
      appaymentId: settledAppaymentId
    });
    return { success: true, written: 0, appaymentIds: [], reason: "already_settled" };
  }
  let completion3;
  try {
    completion3 = await computePromoPayouts(executor, dtp, logger);
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : String(err) };
  }
  if (completion3.outcome !== COMPLETION_OUTCOME.SETTLED) {
    const reason = completion3.reason ?? "unknown";
    logger?.warn("runPromoPayout: completion algorithm held; payout deferred", {
      driverstopromosId,
      reason
    });
    return { success: false, error: `indeterminate:${reason}` };
  }
  if (completion3.newStatus === DRIVERSTOPROMOS_STATUS.FAILED) {
    logger?.info("runPromoPayout: commitment failed; no appayment written", { driverstopromosId });
    return {
      success: true,
      written: 0,
      appaymentIds: [],
      reason: completion3.reason ?? "commitment_failed"
    };
  }
  const payouts = completion3.payouts;
  if (payouts.length === 0) {
    logger?.info("runPromoPayout: nothing owed; no appayment written", {
      driverstopromosId,
      reason: completion3.reason
    });
    return {
      success: true,
      written: 0,
      appaymentIds: [],
      reason: completion3.reason ?? "floor_met"
    };
  }
  const appaymentIds = [];
  for (const payout of payouts) {
    try {
      const id = await writePromoPayout(executor, dtp, payout, hasuraEventId, logger);
      if (id != null) appaymentIds.push(id);
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }
  return {
    success: true,
    written: appaymentIds.length,
    appaymentIds,
    ...appaymentIds.length === 0 ? { reason: "all_existing" } : {}
  };
}
async function fetchDriverstopromoForPayout(executor, driverstopromosId, logger) {
  try {
    const dtp = await getDriverstopromoForPayout({ id: driverstopromosId }, { executor });
    if (!dtp || !dtp.promo) return null;
    return dtp;
  } catch (err) {
    logger?.error("runPromoPayout: failed to fetch driverstopromos for AP", err, {
      driverstopromosId
    });
    return null;
  }
}
async function findPromoWindowAppayment(executor, dtp, logger) {
  try {
    const rows = await getPromoWindowAppayment(
      { driver_id: dtp.driver_id, promo_id: dtp.promo_id },
      { executor }
    );
    return rows?.[0]?.id ?? null;
  } catch (err) {
    logger?.warn("runPromoPayout: window payout probe failed; running the full settlement instead", {
      driverstopromosId: dtp.id,
      error: err
    });
    return null;
  }
}
async function computePromoPayouts(executor, dtp, logger) {
  const promo = dtp.promo;
  const result = await runCompletion(promo, {
    driverstopromo: dtp,
    promo,
    client: executor,
    logger
  });
  return {
    ...result,
    payouts: Array.isArray(result?.payouts) ? result.payouts : []
  };
}
async function writePromoPayout(executor, dtp, payout, hasuraEventId, logger) {
  if (typeof payout?.amount !== "number" || payout.amount <= 0) return null;
  const notes = payout.notes || `Promo payment for driverstopromos #${dtp.id}`;
  const existing = await getExistingPromoAppayment(
    { driver_id: dtp.driver_id, promo_id: dtp.promo_id, notes },
    { executor }
  );
  if (existing?.[0]) {
    logger?.info("runPromoPayout: promo appayment already exists \u2014 skipping", {
      notes,
      driverId: dtp.driver_id,
      promoId: dtp.promo_id
    });
    return null;
  }
  const inserted = await insertPromoAppayment(
    {
      driver_id: dtp.driver_id,
      promo_id: dtp.promo_id,
      // numeric column: round to cents but keep it a number for the codegen `numeric → number` scalar.
      amount: Number(payout.amount.toFixed(2)),
      notes,
      author: APPAYMENT_AUTHOR_ENGINE,
      source_event_id: hasuraEventId,
      status: APPAYMENT_STATUS_UNPAID,
      active: APPAYMENT_ACTIVE
    },
    { executor }
  );
  const insertedId = inserted?.id ?? null;
  logger?.info("runPromoPayout: promo appayment written", {
    appaymentId: insertedId,
    driverId: dtp.driver_id,
    promoId: dtp.promo_id,
    amount: payout.amount,
    notes
  });
  return insertedId;
}

// src/expiration/runPromoExpirations.ts
import { resolveExecutor as resolveExecutor3 } from "@hopdrive/sdk-core";
async function runPromoExpirations(promoId, options = {}) {
  const { logger } = options;
  const executor = resolveExecutor3(options.executor);
  if (!promoId) {
    return { expired: false, reason: "missing_id" };
  }
  const promo = await getPromoForExpiration({ id: promoId }, { executor });
  if (!promo?.id) {
    logger?.info("runPromoExpirations: promo not found, skipping", { promoId });
    return { expired: false, reason: "not_found", promoId };
  }
  if (promo.status !== PROMO_STATUS.ACTIVE) {
    return { expired: false, reason: "not_active", promoId };
  }
  const now = /* @__PURE__ */ new Date();
  const endTimePassed = promo.end_time != null && new Date(promo.end_time) < now;
  const capReached = promo.end_time == null && promo.max_acceptances != null && typeof promo.accepted_count === "number" && promo.accepted_count >= promo.max_acceptances;
  if (!endTimePassed && !capReached) {
    return { expired: false, reason: "not_yet_expired", promoId };
  }
  const trigger = endTimePassed ? "end_time" : "cap_reached";
  await expirePromo({ id: promoId, expired: PROMO_STATUS.EXPIRED }, { executor });
  logger?.info("runPromoExpirations: promo expired", { promoId, trigger });
  return { expired: true, reason: "expired", promoId, trigger };
}
export {
  ALGORITHM_KEY,
  ALGORITHM_TYPE,
  COMPLETION_OUTCOME,
  COUNTING_STATUSES,
  CUSTOMER_TIER,
  DRIVERSTOPROMOS_STATUS,
  DRIVER_TAX_CLASS,
  ELIGIBILITY_REASON,
  GUARANTEED_PAY_BLOCK_SETTLE_GRACE_HOURS,
  HOLD_INDETERMINATE_REASONS,
  HOLD_IN_FLIGHT_REASONS,
  MOVE_STATUSES_FINISHED,
  MOVE_STATUSES_NOT_IN_PROGRESS,
  MOVE_STATUSES_NOT_STARTED,
  PROMO_STATUS,
  PROMO_TEMPLATE_KEY,
  SETTLED_REASONS,
  TEMPLATE_BOOST_CONFIG,
  TEMPLATE_EXCLUDED_TAX_CLASSES,
  TEMPLATE_REGISTRY,
  TERMINAL_DROP_STATUSES,
  algorithmRegistry,
  getAlgorithm,
  resolveBoostWeight,
  runCompletion,
  runEligibility,
  runPromoExpirations,
  runPromoPayout
};
//# sourceMappingURL=index.mjs.map